import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ConversionPanel from './components/ConversionPanel';
import { conversionCategories } from './data/conversionData';
import { fetchCurrencyRates } from './services/currencyService';

const { FiCalculator, FiCopy, FiCheckCircle } = FiIcons;

function App() {
  const [activeCategory, setActiveCategory] = useState('length');
  const [currencyRates, setCurrencyRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [copiedValue, setCopiedValue] = useState('');

  useEffect(() => {
    // Track initial page view
    if (window.gtag) {
      window.gtag('config', 'G-SHSTZKNC7N', {
        page_path: '/'
      });
    }
  }, []);

  useEffect(() => {
    if (activeCategory === 'currency') {
      loadCurrencyRates();
    }
    
    // Track category change as page view
    if (window.gtag) {
      window.gtag('config', 'G-SHSTZKNC7N', {
        page_path: `/${activeCategory}`
      });
      
      // Track category change as event
      window.gtag('event', 'Category Change', {
        event_category: 'Navigation',
        event_label: activeCategory
      });
    }
  }, [activeCategory]);

  const loadCurrencyRates = async () => {
    setLoading(true);
    try {
      const rates = await fetchCurrencyRates();
      setCurrencyRates(rates);
      if (window.gtag) {
        window.gtag('event', 'Currency Rates Loaded', {
          event_category: 'Data',
          event_label: 'Success'
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Währungskurse:', error);
      if (window.gtag) {
        window.gtag('event', 'Currency Rates Loading Failed', {
          event_category: 'Error',
          event_label: error.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value.toString());
      setCopiedValue(value);
      setTimeout(() => setCopiedValue(''), 2000);
      
      // Track copy event
      if (window.gtag) {
        window.gtag('event', 'Copy Value', {
          event_category: 'Interaction',
          event_label: activeCategory,
          value: parseFloat(value)
        });
      }
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
    }
  };

  // Sort categories alphabetically by their display names
  const sortedCategoryKeys = Object.keys(conversionCategories).sort((a, b) =>
    conversionCategories[a].name.localeCompare(conversionCategories[b].name)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header with Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-blue-600 mb-4">
            umrechnungen.de
          </h1>
          <p className="text-gray-600 text-lg">
            Umrechnung aller gaengigen Masseinheiten. Umrechnen von Laengen, Gewichten, Druck, Waehrungen und vielem mehr
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex flex-wrap justify-center gap-2 p-2 bg-white rounded-2xl shadow-lg max-w-4xl mx-auto">
            {sortedCategoryKeys.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {conversionCategories[category].name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Conversion Panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ConversionPanel
              category={activeCategory}
              data={conversionCategories[activeCategory]}
              currencyRates={currencyRates}
              loading={loading}
              onCopy={handleCopy}
              copiedValue={copiedValue}
            />
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12 text-gray-500"
        >
          <p>umrechnungen.de - Erstellt mit ❤️ unter Verwendung moderner Webtechnologien</p>
          {activeCategory === 'currency' && (
            <p className="text-sm mt-2">
              Währungskurse bereitgestellt von exchangerate.host
            </p>
          )}
        </motion.footer>
      </div>
    </div>
  );
}

export default App;